import React, { useState } from 'react';
import { IconButton, Popover, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb as faLightbulbSolid } from '@fortawesome/free-solid-svg-icons';
import { faLightbulb as faLightbulbOutline } from '@fortawesome/pro-light-svg-icons';
import MssTooltip from './MssTooltip';

/**
 * A lightbulb that reveals its hint in a popover. With nothing to reveal the
 * button is disabled, and its tooltip says so rather than leaving the learner
 * clicking a button that does nothing.
 *
 * Props:
 *   id         (string) - ID for the button; the tooltip and popover derive theirs from it.
 *   hint       (string) - The hint text. Empty or missing disables the button.
 *   label      (string) - Tooltip and aria text while a hint is available.
 *   emptyLabel (string) - Tooltip and aria text while there is none.
 */
export const HintButton = ({ id, hint, label = 'Show hint', emptyLabel = 'No hint available' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  if (!hint) {
    return (
      // Wrapped in a span so the tooltip still fires on hover — a disabled
      // button doesn't receive pointer events on its own.
      <MssTooltip id={`${id}__tooltip_hint`} title={emptyLabel}>
        <span>
          <IconButton
            id={`${id}__iconButton_hint`}
            className="toolbarIconButton"
            size="small"
            disabled
            aria-label={emptyLabel}
          >
            <FontAwesomeIcon icon={faLightbulbOutline} />
          </IconButton>
        </span>
      </MssTooltip>
    );
  }

  return (
    <>
      <MssTooltip id={`${id}__tooltip_hint`} title={label}>
        <IconButton
          id={`${id}__iconButton_hint`}
          className="toolbarIconButton"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          aria-label={label}
        >
          <FontAwesomeIcon icon={isOpen ? faLightbulbSolid : faLightbulbOutline} />
        </IconButton>
      </MssTooltip>
      <Popover
        id={`${id}__popover_hint`}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        className="mss__studyPopover"
      >
        <Typography id={`${id}__text_hintPopover`} className="popoverText">
          <strong>Hint:</strong> {hint}
        </Typography>
      </Popover>
    </>
  );
};
